<template>
  <transition name="cart-slide">
    <div v-if="isOpen" class="cart-overlay" @click.self="close">
      <div class="cart-panel">
        
        <div class="cart-header">
          <div class="cart-header-left">
            <h2 class="cart-title">Tu Selección</h2>
            <span class="cart-count">{{ cart.length }} Artículos en la bolsa</span>
          </div>
          <button class="close-btn" @click="close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2">
              <path d="M18 6L6 18M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div class="cart-items">
          <div v-for="p in cart" :key="p.variantId || p.id" class="cart-item" :class="{ 'item-invalid': p.validationStatus === 'INVALID' }">
            
            <div class="cart-img-wrapper">
              <span v-if="p.tags && p.tags.some(t => t.startsWith('promo-'))" class="luxury-badge">
                {{ promoText(p) }}
              </span>
              <img :src="p.image" class="cart-img" />
            </div>

            <div class="cart-info">
              <div class="info-top">
                <p class="item-title">{{ p.title }}</p>
                <button class="remove-link" @click="remove(p)">Remover</button>
              </div>

              <p v-if="p.variantTitle" class="item-variant">Talla: {{ p.variantTitle }}</p>
              
              <p v-if="p.availableInStore === false || p.validationStatus === 'INVALID'" class="status-msg">
                Agotado temporalmente
              </p>

              <div class="item-meta">
                <div class="qty-selector">
                  <button @click="decrement(p)">—</button>
                  <span class="qty-display">{{ p.qty }}</span>
                  <button @click="increment(p)">+</button>
                </div>
                
                <p class="item-price">${{ formatPrice(p.price * p.qty) }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="cart-footer">
          
          <div class="shipping-section">
            <div class="shipping-row">
              <span class="shipping-label">Envío a Domicilio</span>
              <span v-if="subtotal < 499" class="shipping-value">${{ formatPrice(50) }}</span>
              <span v-else class="shipping-value free-text">Cortesía</span>
            </div>

            <div v-if="subtotal < 499" class="promo-shipping-container">
              <p class="promo-shipping-text">
                Estás a <strong>${{ formatPrice(499 - subtotal) }}</strong> del envío sin costo.
              </p>
              <div class="progress-bg">
                <div class="progress-bar" :style="{ width: (subtotal / 499 * 100) + '%' }"></div>
              </div>
            </div>

            <div class="shipping-row pickup-row">
             
              <span class="shipping-value free-text">Sin costo</span>
            </div>
          </div>

          <div class="summary-row">
            <span class="total-label">Subtotal</span>
<span class="total-amount">
  ${{ formatPrice(subtotal) }}
  <small class="currency">USD</small>
</span>          </div>

          <p class="tax-note">Impuestos y gastos de envío finales se calculan al finalizar.</p>

          <div class="footer-actions">
            <button 
              class="main-checkout-btn" 
              :disabled="hasInvalidProducts || goingToCheckout"
              @click="goToCheckout"
            >
              <span v-if="!goingToCheckout">Confirmar Pedido</span>
              <span v-else class="loader-text">Procesando...</span>
            </button>
          </div>

          <div v-if="hasInvalidProducts" class="error-alert">
            Algunos artículos no están disponibles para la tienda seleccionada.
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { eventBus } from "@/eventBus";
import axiosClient from "@/api/axiosClient";
import { useCart } from "@/components/composables/useCart";
import { useLocationStore } from "@/stores/location";

const cartApi = useCart();

export default {
  name: "CartSidebar",

  data() {
    return {
      goingToCheckout: false,
    };
  },

  computed: {
    // 🔥 ESCUCHA AL BUS DIRECTAMENTE
    isOpen() {
      return eventBus.isOpen; 
    },
    hasInvalidProducts() {
      return this.cart.some(p => p.validationStatus === "INVALID");
    },
    locationStore() {
      return useLocationStore();
    },
    cart() {
      return cartApi.cart;
    },
    subtotal() {
      return this.cart.reduce((sum, p) => sum + p.qty * p.price, 0);
    }
  },

  methods: {
    formatPrice(value) {
      if (typeof value !== "number") value = parseFloat(value) || 0;
      return value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    promoText(p) {
      if (!Array.isArray(p.tags)) return "";
      const tag = p.tags.find(t => t.startsWith("promo-"));
      if (!tag) return "";
      const txt = tag.replace("promo-", "").replace(/-/g, " ");
      return /^\d+$/.test(txt) ? `${txt}% OFF` : txt.toUpperCase();
    },

    // 🔥 USAMOS EL BUS PARA MANEJAR EL ESTADO
    open() { eventBus.openCart(); },
    close() { eventBus.closeCart(); },

    increment(p) {
      cartApi.upsertItem({
        product: p, qty: p.qty + 1, unit: p.unit, price: p.price,
        variantId: p.variantId, variantTitle: p.variantTitle, variantes: p.variantes
      });
    },

    decrement(p) {
      if (p.qty > 1) {
        cartApi.upsertItem({
          product: p, qty: p.qty - 1, unit: p.unit, price: p.price,
          variantId: p.variantId, variantTitle: p.variantTitle, variantes: p.variantes
        });
      } else {
        this.remove(p);
      }
    },

    remove(p) {
      const productId = p.id || p.product_id;
      cartApi.removeItem(productId, p.variantId);
    },

async goToCheckout(e) {
  e?.preventDefault();
  const locationId = this.locationStore.selectedStore?.id;
  if (!locationId || !this.cart.length) return;

  this.goingToCheckout = true;

  try {
    const validation = await axiosClient.post("/shopify/cart/validate", {
      locationId,
      items: this.cart.map(p => ({
        variantId: p.variantId,
        qty: Math.max(1, Math.round(p.qty))
      }))
    });

    validation.data.items.forEach(v => {
      const item = this.cart.find(p => p.variantId === v.variantId);
      if (item) item.validationStatus = v.valid ? "OK" : "INVALID";
    });

    if (this.hasInvalidProducts) {
      this.goingToCheckout = false;
      return;
    }

    sessionStorage.setItem("checkout_location_id", locationId);
    this.close();
    this.$router.push({ name: "CustomCheckout" });

  } catch (err) {
    console.error("Error checkout:", err);
  } finally {
    this.goingToCheckout = false;
  }
}
  },

  mounted() {
    this.locationStore.loadFromStorage();
    window.addEventListener("pageshow", () => {
      if (sessionStorage.getItem("checkout_success") === "1") {
        cartApi.clearCart();
        sessionStorage.removeItem("checkout_success");
      }
    });
  }
};
</script>
<style scoped>
/* Tipografía y Base */
.cart-panel {
  font-family: 'Inter', sans-serif;
  width: 420px;
  background: #ffffff;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Header Estilo Boutique */
.cart-header {
  padding: 60px 40px 30px;
  display: flex;
  justify-content: space-between;
}

.cart-title {
  font-family: 'Playfair Display', serif;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.cart-count {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #888;
  margin-top: 5px;
  display: block;
}

.close-btn {
  background: none; border: none;
  cursor: pointer; color: #000;
  transition: opacity 0.2s;
}

.close-btn:hover { opacity: 0.5; }

/* Lista de Productos */
.cart-items {
  flex: 1;
  overflow-y: auto;
  padding: 0 40px;
}

.cart-item {
  display: flex;
  gap: 20px;
  padding: 25px 0;
  border-bottom: 1px solid #f9f9f9;
}

.cart-img-wrapper {
  width: 80px;
  height: 100px;
  flex-shrink: 0;
}

.cart-img {
  width: 100%; height: 100%;
  object-fit: cover;
}

.luxury-badge {
  position: absolute;
  background: #000; color: #fff;
  font-size: 8px; padding: 3px 6px;
  text-transform: uppercase;
}

.item-title {
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 4px;
}

.remove-link {
  background: none; border: none;
  font-size: 10px; text-transform: uppercase;
  color: #bbb; cursor: pointer;
  letter-spacing: 0.8px;
  padding: 0;
}

.item-variant {
  font-size: 12px; color: #888;
}

.item-meta {
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.qty-selector {
  display: flex;
  align-items: center;
  border: 1px solid #eee;
}

.qty-selector button {
  width: 28px; height: 28px;
  background: none; border: none; cursor: pointer;
}

.qty-display { font-size: 12px; font-weight: 500; width: 20px; text-align: center; }

.item-price { font-size: 14px; font-weight: 600; color: #000; }
/* Footer y Sección de Envío */
/* Reducimos el padding general del footer */
.cart-footer {
  padding: 15px 25px 25px; /* Antes era 25px 30px 40px */
  background: #fff;
  border-top: 1px solid #f5f5f5;
}

/* Hacemos las filas de envío más pegadas */
.shipping-section {
  margin-bottom: 10px; /* Antes 18px */
}

.shipping-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px; /* Espacio mínimo entre filas */
}

/* Letra un poco más pequeña para las etiquetas */
.shipping-label {
  font-size: 9px; /* Bajamos de 10px a 9px */
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #999;
}

.shipping-value {
  font-size: 10px;
  font-weight: 600;
}

/* Ajuste para que el texto de 'Recoger en Tienda' no use dos líneas */
.pickup-row {
  align-items: baseline;
}

.pickup-subtext {
  display: inline; /* Lo ponemos en línea */
  font-size: 9px;
  color: #ccc;
  margin-left: 5px; /* Separación del label principal */
}
.free-text {
  color: #000;
  font-weight: 500;
  font-size: 11px;
}



/* Barra de progreso de envío */
.promo-shipping-container {
  background: transparent; /* Quitamos el fondo gris para que no pese visualmente */
  margin-bottom: 8px;
}

.promo-shipping-text {
  font-size: 11px; /* Bajamos de 12px a 11px */
  color: #666;
  margin-bottom: 4px;
}

.progress-bg {
  height: 1px; /* Barra súper fina de 1px (estilo lujo) */
  background: #eee;
}

.progress-bar {
  height: 1px;
}
.progress-bar {
  background: #000;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 20px;
}

.total-label {
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
}

.total-amount {
  font-family: 'Playfair Display', serif;
  font-size: 28px;
  font-weight: 700;
}

.tax-note {
  font-size: 11px;
  color: #aaa;
  margin: 10px 0 25px;
  font-style: italic;
}

/* Botón Principal */
.main-checkout-btn {
  width: 100%;
  background: #000;
  color: #fff;
  padding: 22px;
  border: none;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  transition: transform 0.2s, background 0.2s;
}

.main-checkout-btn:hover:not(:disabled) {
  background: #1a1a1a;
  transform: translateY(-1px);
}

.main-checkout-btn:disabled {
  background: #f5f5f5;
  color: #ccc;
  cursor: not-allowed;
}

.error-alert {
  margin-top: 15px;
  font-size: 11px;
  color: #d93025;
  text-align: center;
}

/* Animación Sidebar */
.cart-slide-enter-active, .cart-slide-leave-active {
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}
.cart-slide-enter-from, .cart-slide-leave-to {
  transform: translateX(100%);
}
.cart-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.2);
  display: flex;
  justify-content: flex-end;
  z-index: 9999;
}

/* 📱 TABLET Y CELULAR */
@media (max-width: 768px) {

  .cart-overlay {
    backdrop-filter: blur(8px); /* Desenfoque elegante */
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    justify-content: flex-end; /* Lo pega a la derecha */
    align-items: stretch;      /* Fuerza a que el contenido estire de arriba a abajo */
  }

  .cart-panel {
    width: 90%;                /* Ocupa el 90% del ancho */
    max-width: 100%;           /* Evita restricciones de escritorio */
    height: 100vh;             /* 100% de altura, pegado a los bordes */
    
    border-radius: 0;          /* Quitamos curvas para que se vea arquitectónico/lujo */
    
    /* Sombra profunda lateral para dar volumen */
    box-shadow: -15px 0 50px rgba(0, 0, 0, 0.3);
    
    overflow: hidden;
    position: relative;
  }

  /* Ajuste de padding en móvil para que no se vea apretado */
  .cart-header {
    padding: 30px 20px;
  }
  
  .cart-items {
    padding: 10px 20px;
  }
}

/* --- EFECTO DE LUJO AL ABRIR --- */

/* Transición sincronizada */
.cart-slide-enter-active, 
.cart-slide-leave-active {
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1); /* Curva premium (más lenta al final) */
}

/* El fondo aparece con fade */
.cart-slide-enter-from, 
.cart-slide-leave-to {
  opacity: 0;
}
.currency {
  font-size: 0.7rem;
  margin-left: 4px;
  opacity: 0.7;
  vertical-align: middle; /* 👈 CAMBIA ESTO */
  letter-spacing: 1px;
}
/* El panel entra desde la derecha con un ligero retraso visual */
.cart-slide-enter-from .cart-panel,
.cart-slide-leave-to .cart-panel {
  transform: translateX(100%);
}

/* Eliminar el gradiente ::after si quieres limpieza absoluta */
.cart-overlay::after {
  display: none; 
}
</style>